import React, { useEffect, useState, useCallback } from 'react';
import { Bot, Loader, Check, X as XIcon, RefreshCw, Play, Trash2, Plus } from 'lucide-react';
import { io } from 'socket.io-client';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';

const TABS = [
  { key: 'proposals', label: 'Needs approval' },
  { key: 'activity', label: 'Activity' },
  { key: 'agents', label: 'Agents' },
  { key: 'memory', label: 'What they know' },
];
const TIER_BADGE = { write: 'sf-badge-manager', send: 'sf-badge-warning', money: 'sf-badge-warning', destructive: 'sf-badge-error' };
const OUTCOME_BADGE = { answered: 'sf-badge-success', pending_approval: 'sf-badge-warning', error: 'sf-badge-error', out_of_credits: 'sf-badge-error', blocked: 'sf-badge-error' };
const MODES = ['auto', 'approve', 'deny'];
const TIERS = ['write', 'send', 'money', 'destructive'];
const ENTITY = ['lead', 'deal', 'contact', 'account'];
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const err = (e, d = 'Something went wrong') => e?.response?.data?.error || d;
const notify = () => window.dispatchEvent(new Event('agents:changed'));

function Proposals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/agents/proposals', { params: { status: 'pending' } })).data.data || []); } catch (e) { alertService.error(err(e, 'Could not load proposals')); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    const socket = io('/inbox', { auth: { token }, transports: ['websocket'], reconnectionAttempts: 5 });
    socket.on('agent:approval_requested', () => load());
    return () => socket.disconnect();
  }, [load]);

  const decide = async (it, action) => {
    let reason = '';
    if (action === 'reject') reason = window.prompt('Why not? (the agent will remember this)') || '';
    else if (!(await alertService.confirm('Approve this action?', `Run this ${it.tier} action now?\n\n${it.summary}`, it.tier === 'write' ? 'warning' : 'danger', 'Approve'))) return;
    setBusy(it._id);
    try {
      await api.post(`/support/approvals/${it._id}/${action}`, action === 'reject' ? { reason } : {});
      alertService.success(action === 'approve' ? 'Approved and done' : 'Rejected');
    } catch (e) { alertService.error(err(e, 'Could not complete that')); } finally { setBusy(null); await load(); notify(); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader className="animate-spin text-[#0176D3]" size={20} /></div>;
  if (!items.length) return <p className="p-8 text-center text-[13px] text-[#706E6B]">Nothing is waiting for you. When an agent wants to message a customer, move money or delete something, it shows up here first.</p>;
  return (
    <table className="w-full text-[13px]">
      <thead><tr className="text-left text-[12px] text-[#706E6B] border-b border-[#E5E5E5]"><th className="p-3">What the agent wants to do</th><th className="p-3">Risk</th><th className="p-3">Agent</th><th className="p-3">Asked</th><th className="p-3" /></tr></thead>
      <tbody>
        {items.map((it) => (
          <tr key={it._id} className="border-b border-[#F3F2F2] align-top">
            <td className="p-3 font-mono text-[12px] break-all">{it.summary}</td>
            <td className="p-3"><span className={`sf-badge ${TIER_BADGE[it.tier] || ''}`}>{it.tier}</span></td>
            <td className="p-3">{String(it.requestedBy || '').replace('agent:', '') || '—'}</td>
            <td className="p-3 whitespace-nowrap">{fmt(it.createdAt)}</td>
            <td className="p-3 whitespace-nowrap text-right">
              <button type="button" disabled={busy === it._id} onClick={() => decide(it, 'approve')} className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-[#2E844A] hover:bg-[#256B3B] px-2.5 py-1.5 rounded mr-2 disabled:opacity-50"><Check size={13} /> Approve</button>
              <button type="button" disabled={busy === it._id} onClick={() => decide(it, 'reject')} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#BA0517] border border-[#BA0517] px-2.5 py-1.5 rounded disabled:opacity-50"><XIcon size={13} /> Reject</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Activity({ agents }) {
  const [rows, setRows] = useState(null);
  const [agent, setAgent] = useState('');
  useEffect(() => {
    api.get('/agents/activity', { params: agent ? { agent } : {} }).then((r) => setRows(r.data.data || [])).catch(() => setRows([]));
  }, [agent]);
  return (
    <div>
      <div className="p-3 border-b border-[#E5E5E5]">
        <select className="text-[13px] border border-[#D8DDE6] rounded px-2 py-1.5" value={agent} onChange={(e) => setAgent(e.target.value)}>
          <option value="">All agents</option>
          {agents.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
        </select>
      </div>
      {rows === null ? <div className="p-8 flex justify-center"><Loader className="animate-spin text-[#0176D3]" size={20} /></div>
        : !rows.length ? <p className="p-8 text-center text-[13px] text-[#706E6B]">No agent activity yet.</p> : (
          <table className="w-full text-[13px]">
            <thead><tr className="text-left text-[12px] text-[#706E6B] border-b border-[#E5E5E5]"><th className="p-3">When</th><th className="p-3">Agent</th><th className="p-3">Record</th><th className="p-3">Outcome</th><th className="p-3">Actions</th><th className="p-3">Credits</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} className="border-b border-[#F3F2F2]">
                  <td className="p-3 whitespace-nowrap">{fmt(r.createdAt)}</td><td className="p-3">{r.agentKey}</td>
                  <td className="p-3">{r.entity?.type ? `${r.entity.type} …${String(r.entity.id).slice(-6)}` : '—'}</td>
                  <td className="p-3"><span className={`sf-badge ${OUTCOME_BADGE[r.outcome] || ''}`}>{String(r.outcome || '').replace('_', ' ')}</span></td>
                  <td className="p-3">{(r.steps || []).filter((s) => s.kind === 'tool').map((s) => s.name).join(', ') || '—'}</td>
                  <td className="p-3">{r.creditsCharged || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

function AgentCard({ agent, onChange }) {
  const [form, setForm] = useState({ enabled: agent.enabled, dryRun: agent.dryRun, autonomy: agent.autonomy, dailyCredits: agent.budget.dailyCredits, maxSteps: agent.budget.maxSteps });
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState({ entityType: 'lead', entityId: '', result: null, running: false });
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/agents/${agent.key}`, { enabled: form.enabled, dryRun: form.dryRun, autonomy: Object.fromEntries(TIERS.map((t) => [t, form.autonomy[t]])), budget: { dailyCredits: Number(form.dailyCredits), maxSteps: Number(form.maxSteps) } });
      alertService.success('Saved');
      onChange();
    } catch (e) { alertService.error(err(e, 'Could not save')); } finally { setSaving(false); }
  };
  const runTest = async () => {
    setTest((t) => ({ ...t, running: true, result: null }));
    try { const r = await api.post(`/agents/${agent.key}/test`, { entityType: test.entityType, entityId: test.entityId.trim() }); setTest((t) => ({ ...t, running: false, result: r.data.data })); } catch (e) { setTest((t) => ({ ...t, running: false })); alertService.error(err(e, 'Test failed')); }
  };
  return (
    <div className="sf-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-bold text-[#2E2E2E]">{agent.name}</h3>
          <p className="text-[12px] text-[#706E6B] max-w-xl">{agent.description}</p>
          <p className="text-[11px] text-[#706E6B] mt-1">Runs on: {[...agent.triggers, ...agent.schedules.map((s) => `schedule ${s}`)].join(', ') || 'manual only'}</p>
        </div>
        <label className="flex items-center gap-2 text-[13px] font-semibold shrink-0"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> {form.enabled ? 'On' : 'Off'}</label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
        <div><span className="text-[#706E6B]">Runs today</span><div className="text-lg font-bold">{agent.today.runs}</div></div>
        <div><span className="text-[#706E6B]">Credits today</span><div className="text-lg font-bold">{agent.today.credits}</div></div>
        <div><span className="text-[#706E6B]">Accepted</span><div className="text-lg font-bold">{agent.feedback.acceptanceRate == null ? '—' : `${agent.feedback.acceptanceRate}%`}</div></div>
        <div><span className="text-[#706E6B]">Rejected</span><div className="text-lg font-bold">{agent.feedback.rejected}</div></div>
      </div>
      <div>
        <p className="text-[12px] font-semibold text-[#444] mb-2">What it may do on its own</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TIERS.map((t) => (
            <label key={t} className="text-[12px]"><span className="block text-[#706E6B] capitalize mb-1">{t === 'send' ? 'Message customers' : t === 'money' ? 'Move money' : t === 'destructive' ? 'Delete things' : 'Change records'}</span>
              <select className="w-full border border-[#D8DDE6] rounded px-2 py-1.5 text-[13px]" value={form.autonomy[t]} onChange={(e) => setForm({ ...form, autonomy: { ...form.autonomy, [t]: e.target.value } })}>
                {MODES.map((m) => <option key={m} value={m}>{m === 'auto' ? 'Do it' : m === 'approve' ? 'Ask me first' : 'Never'}</option>)}
              </select>
            </label>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-4 text-[12px]">
        <label>Daily credits<input type="number" min="0" className="block w-28 border border-[#D8DDE6] rounded px-2 py-1.5 text-[13px] mt-1" value={form.dailyCredits} onChange={(e) => setForm({ ...form, dailyCredits: e.target.value })} /></label>
        <label>Max steps<input type="number" min="1" max="12" className="block w-24 border border-[#D8DDE6] rounded px-2 py-1.5 text-[13px] mt-1" value={form.maxSteps} onChange={(e) => setForm({ ...form, maxSteps: e.target.value })} /></label>
        <label className="flex items-center gap-2 pb-2"><input type="checkbox" checked={form.dryRun} onChange={(e) => setForm({ ...form, dryRun: e.target.checked })} /> Practice mode (nothing is changed or sent)</label>
        <button type="button" onClick={save} disabled={saving} className="ml-auto h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
      </div>
      <div className="border-t border-[#E5E5E5] pt-3">
        <p className="text-[12px] font-semibold text-[#444] mb-2">Try it on a record (practice run: it looks, it does not act)</p>
        <div className="flex flex-wrap gap-2 items-center">
          <select className="border border-[#D8DDE6] rounded px-2 py-1.5 text-[13px]" value={test.entityType} onChange={(e) => setTest({ ...test, entityType: e.target.value })}>{ENTITY.map((e) => <option key={e}>{e}</option>)}</select>
          <input className="border border-[#D8DDE6] rounded px-2 py-1.5 text-[13px] w-72" placeholder="Record id (from the record's web address)" value={test.entityId} onChange={(e) => setTest({ ...test, entityId: e.target.value })} />
          <button type="button" onClick={runTest} disabled={test.running || test.entityId.trim().length < 12} className="inline-flex items-center gap-1 h-9 px-3 border border-[#0176D3] text-[#0176D3] rounded text-[13px] font-semibold disabled:opacity-50"><Play size={13} /> {test.running ? 'Running…' : 'Run'}</button>
        </div>
        {test.result && <p className="mt-2 text-[12px] text-[#444]"><b>{String(test.result.status).replace('_', ' ')}.</b> {test.result.summary || test.result.reason || ''} {test.result.steps ? `(${test.result.steps} steps)` : ''}</p>}
      </div>
    </div>
  );
}

function Agents({ agents, reload }) {
  if (!agents.length) return <p className="p-8 text-center text-[13px] text-[#706E6B]">No agents are available yet.</p>;
  return <div className="p-4 space-y-4">{agents.map((a) => <AgentCard key={`${a.key}-${a.enabled}-${a.dryRun}`} agent={a} onChange={reload} />)}</div>;
}

function Memory({ agents }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState('');
  const [scope, setScope] = useState('tenant');
  const [agentKey, setAgentKey] = useState('');
  const load = useCallback(() => api.get('/agents/memory').then((r) => setRows(r.data.data || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post('/agents/memory', { scope, agentKey: scope === 'agent' ? agentKey : undefined, text }); setText(''); load(); } catch (e) { alertService.error(err(e, 'Could not save')); }
  };
  const remove = async (id) => { try { await api.delete(`/agents/memory/${id}`); load(); } catch (e) { alertService.error(err(e)); } };
  return (
    <div className="p-4 space-y-4">
      <p className="text-[12px] text-[#706E6B]">Tell agents how your business works. They read this before every job. Rules also appear here automatically when you reject a proposal and say why.</p>
      <div className="flex flex-wrap gap-2">
        <select className="border border-[#D8DDE6] rounded px-2 py-1.5 text-[13px]" value={scope} onChange={(e) => setScope(e.target.value)}><option value="tenant">All agents</option><option value="agent">One agent</option></select>
        {scope === 'agent' && <select className="border border-[#D8DDE6] rounded px-2 py-1.5 text-[13px]" value={agentKey} onChange={(e) => setAgentKey(e.target.value)}><option value="">Choose…</option>{agents.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}</select>}
        <input className="flex-1 min-w-[260px] border border-[#D8DDE6] rounded px-2 py-1.5 text-[13px]" maxLength={600} placeholder="e.g. We never offer discounts in the first message" value={text} onChange={(e) => setText(e.target.value)} />
        <button type="button" disabled={!text.trim() || (scope === 'agent' && !agentKey)} onClick={add} className="inline-flex items-center gap-1 h-9 px-3 bg-[#0176D3] text-white rounded text-[13px] font-semibold disabled:opacity-50"><Plus size={13} /> Add</button>
      </div>
      {!rows.length ? <p className="text-[13px] text-[#706E6B]">Nothing yet.</p> : (
        <ul className="divide-y divide-[#F3F2F2] border border-[#E5E5E5] rounded">
          {rows.map((m) => (
            <li key={m._id} className="p-3 flex items-start justify-between gap-3 text-[13px]">
              <div><div>{m.text}</div><div className="text-[11px] text-[#706E6B]">{m.scope === 'agent' ? `agent: ${m.agentKey}` : m.scope} · {m.kind.replace('_', ' ')} · {fmt(m.createdAt)}</div></div>
              <button type="button" onClick={() => remove(m._id)} className="text-[#BA0517]" aria-label="Remove"><Trash2 size={15} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AgentHubPage() {
  const [tab, setTab] = useState('proposals');
  const [agents, setAgents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [locked, setLocked] = useState(false);
  const reload = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([api.get('/agents'), api.get('/agents/summary')]);
      setAgents(a.data.data || []); setSummary(s.data.data); setLocked(false);
    } catch (e) { if (e?.response?.status === 403) setLocked(true); else alertService.error(err(e, 'Could not load agents')); }
  }, []);
  useEffect(() => { reload(); const f = () => reload(); window.addEventListener('agents:changed', f); return () => window.removeEventListener('agents:changed', f); }, [reload]);

  if (locked) return <div className="p-6"><PageHeader title="Agent Hub" subtitle="Your AI agents" icon={Bot} /><div className="sf-card p-8 text-center text-[13px] text-[#444]">Agents are not included in your plan. Upgrade to switch them on.</div></div>;
  return (
    <div className="p-6">
      <PageHeader title="Agent Hub" subtitle="AI agents that do the routine work, and ask you before anything risky" icon={Bot}
        actions={<button type="button" onClick={reload} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#444444] bg-[#F3F2F2] hover:bg-[#E5E5E5] px-3 py-2 rounded"><RefreshCw size={14} /> Refresh</button>} />
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="sf-card p-4"><div className="text-[11px] text-[#706E6B] uppercase font-bold">Waiting for you</div><div className="text-2xl font-bold">{summary.pendingCount}</div></div>
          <div className="sf-card p-4"><div className="text-[11px] text-[#706E6B] uppercase font-bold">Runs today</div><div className="text-2xl font-bold">{summary.runsToday}</div></div>
          <div className="sf-card p-4"><div className="text-[11px] text-[#706E6B] uppercase font-bold">Accepted</div><div className="text-2xl font-bold">{summary.acceptanceRate == null ? '—' : `${summary.acceptanceRate}%`}</div></div>
          <div className="sf-card p-4"><div className="text-[11px] text-[#706E6B] uppercase font-bold">Agents on</div><div className="text-2xl font-bold">{agents.filter((a) => a.enabled).length}/{agents.length}</div></div>
        </div>
      )}
      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map((t) => <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`text-[12px] px-3 py-1.5 rounded-full border ${tab === t.key ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#444] border-[#C9C9C9]'}`}>{t.label}{t.key === 'proposals' && summary?.pendingCount ? ` (${summary.pendingCount})` : ''}</button>)}
      </div>
      <div className="sf-card overflow-x-auto">
        {tab === 'proposals' && <Proposals />}
        {tab === 'activity' && <Activity agents={agents} />}
        {tab === 'agents' && <Agents agents={agents} reload={reload} />}
        {tab === 'memory' && <Memory agents={agents} />}
      </div>
    </div>
  );
}
